import { FunctionComponent, useEffect, useState, useRef } from "react";

import { useAuth } from "@voxmedia/duet-context/context/auth-context";
import { useCoralDrawerContext } from "@voxmedia/duet-context/context/coral-drawer-context";
import { useLoopNavContext } from "@voxmedia/duet-context/context/loop-nav-context";
import { useNavigationContext } from "@voxmedia/duet-ui/context/navigation-context";
import { getCookie, setCookie } from "@voxmedia/duet-utils/cookie";
import useIsomorphicLayoutEffect from "@voxmedia/duet-utils/use-isomorphic-layout-effect";

const ORIGINAL_REFERRER_COOKIE = "x-original-referrer";

/**
 * Gets the original external referrer for the user's session, persisting it
 * in a session cookie so it survives same-site navigation (e.g., user arrives
 * from Reddit then clicks to a second article — document.referrer would then
 * show Vox, not Reddit).
 */
function getOriginalReferrer(): string {
  const existing = getCookie(ORIGINAL_REFERRER_COOKIE);
  if (existing) {
    return existing;
  }

  let origin: string;
  try {
    origin = new URL(document.referrer).origin;
  } catch {
    origin = document.referrer;
  }

  if (origin && origin !== window.location.origin) {
    // Pass 0 days to set a session cookie (no expiry)
    setCookie(ORIGINAL_REFERRER_COOKIE, origin, 0);
  }

  return origin;
}

declare global {
  interface Window {
    zephrBrowser?: {
      run: (params: {
        jwt: string | null;
        customData: {
          articleCategories: (string | undefined)[];
        };
      }) => void;
    };
    Zephr?: {
      accessDetails?: {
        trials: Record<
          string,
          {
            usedInDecision: boolean;
          }
        >;
      };
    };
  }
}

export interface Props {
  articleId: string;
  internalCategories: ReadonlyArray<
    Readonly<{
      slug?: string;
    }>
  >;
  categories: ReadonlyArray<
    Readonly<{
      slug?: string;
    }>
  >;
}

const Zephr: FunctionComponent<Props> = ({
  articleId,
  internalCategories,
  categories,
}) => {
  const auth = useAuth();
  const { closeDrawer } = useNavigationContext();
  const { closeDrawer: closeLoopNav } = useLoopNavContext();
  const { closeDrawer: closeCoralDrawer } = useCoralDrawerContext();
  const previouslyLoggedIn = useRef(false);
  const [isZephrAvailable, setIsZephrAvailable] = useState(false);
  useIsomorphicLayoutEffect(() => {
    if (window.zephrBrowser) {
      setIsZephrAvailable(true);
      return;
    }

    const script = document.querySelector('script[src*="zephr-browser"]');
    if (script) {
      script.addEventListener("load", () => {
        setIsZephrAvailable(true);
      });
    }
  }, []);

  useEffect(() => {
    const isLoggedIn = Boolean(auth.user);
    if (previouslyLoggedIn.current && !isLoggedIn) {
      closeDrawer();
      closeLoopNav();
      closeCoralDrawer();
    }
    previouslyLoggedIn.current = isLoggedIn;
    if (!auth.userLoading && isZephrAvailable) {
      const articleCategories = [...categories, ...internalCategories].map(
        (category) => category.slug,
      );
      let referer;
      try {
        referer = new URL(document.referrer).origin;
      } catch {
        referer = document.referrer;
      }

      const params = {
        jwt: auth.user?.zephrJwt || null,
        customData: {
          articleCategories,
          articleId,
          hostName: window.location.hostname,
          referer,
          originalReferer: getOriginalReferrer(),
          url_path: window.location.pathname,
        },
      };

      window.zephrBrowser!.run(params);
    }
  }, [
    articleId,
    auth.userLoading,
    auth.user,
    internalCategories,
    categories,
    isZephrAvailable,
    closeDrawer,
    closeLoopNav,
    closeCoralDrawer,
  ]);

  return <></>;
};

export default Zephr;
